<template>
  <div>
    <label class="border cursor-pointer rounded-lg p-3 mb-3 w-100">
      <b-form-group class="mb-0">
        <b-form-checkbox v-model="user.ativo" value="1" unchecked-value="0">
          O usuário está ativo e pode acessar a plataforma?
        </b-form-checkbox>
      </b-form-group>
    </label>

    <h4>Identificação</h4>
    <div class="row no-gutters border rounded-lg p-3 mb-3">
      <b-form-group label="Nome:" class="col-12">
        <b-form-input
          v-model="user.name"
          placeholder="Nome"
          :state="validateState('name')"
        />
        <b-form-invalid-feedback>
          Nome é um campo obrigatório.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="E-mail:" class="col-12">
        <b-form-input
          v-model="user.email"
          placeholder="E-mail"
          :state="validateState('email')"
        />
        <b-form-invalid-feedback>
          <span v-if="!$v.user.email.required">
            E-mail é um campo obrigatório.
          </span>
          <span v-if="!$v.user.email.email">E-mail inválido</span>
          <span v-if="!$v.user.email.serverFailed">
            <span
              v-for="message in $v.user.email.$params.serverFailed.message"
              :key="message"
            >
              {{ $t(message) }}
            </span>
          </span>
        </b-form-invalid-feedback>
      </b-form-group>
    </div>

    <h4>Definições de cliente</h4>
    <div class="row no-gutters border rounded-lg p-3 mb-3">
      <b-form-group label="Cliente vinculado:" class="col-12 mb-0">
        <select-cliente
          v-model="user.cliente_id"
          :state="validateState('cliente_id')"
          ref="selectCliente"
          size="md"
        />
        <b-form-invalid-feedback :state="validateState('cliente_id')">
          Cliente é um campo obrigatório.
        </b-form-invalid-feedback>
        <b-form-text v-if="!!user.cliente_id">
          Para editar os dados do cliente selecionado, clique em
          <b-button
            variant="link"
            size="sm"
            class="px-0"
            target="_blank"
            :to="{
              name: 'clientes_edit',
              params: { id: user.cliente_id },
            }"
          >
            editar cliente
            <font-awesome-icon icon="arrow-up-right-from-square" size="sm" />
          </b-button>
        </b-form-text>
        <b-form-text v-else>
          Selecione o cliente que será vinculado ao usuário.
        </b-form-text>
      </b-form-group>
    </div>

    <h4>Segurança e permissões</h4>
    <div class="row no-gutters border rounded-lg p-3 mb-3">
      <view-toggle
        ref="viewToggleRef"
        :initial-value="!user.id"
        reveal-class="w-100"
        class="w-100"
        @on="handlerChangeField(false)"
        @off="handlerChangeField(true)"
      >
        <template #default="{ off }">
          <password-form ref="passwordFormRef" v-model="user" />
          <div class="w-100 d-inline-flex">
            <b-button
              v-if="!!user.id"
              size="sm"
              variant="outline-danger"
              class="mt-3 ml-auto"
              @click="off"
            >
              Cancelar alteração
            </b-button>
          </div>
        </template>
        <template #reveal="{ on, show }">
          <b-alert
            v-if="show"
            show
            class="d-inline-flex rounded-lg bg-transparent border-exitos align-items-center w-100 mb-0"
          >
            <font-awesome-icon icon="key" class="mr-2 text-exitos" size="sm" />
            <div class="font-weight-normal mb-0 text-exitos">
              A senha atual não é exibida por segurança.
            </div>
            <b-button variant="exitos" size="sm" class="ml-auto" @click="on">
              Alterar senha
            </b-button>
          </b-alert>
        </template>
      </view-toggle>
      <hr class="col-12" />
      <b-form-group
        v-if="roles"
        label="Papéis e permissões:"
        class="col-12"
        v-slot="{ ariaDescribedby }"
      >
        <b-form-checkbox-group
          v-model="selectedRoles"
          :options="formattedRoles"
          :aria-describedby="ariaDescribedby"
          stacked
          name="roles"
        />
      </b-form-group>
    </div>

    <div v-show="canUseMap">
      <h4>Municípios habilitados para simulação</h4>
      <select-multi-municipio v-model="user.simulacao_recorte" />
    </div>
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  email,
  sameAs,
  requiredIf,
  helpers,
} from "vuelidate/lib/validators";
import { mapActions, mapState } from "vuex";
import ViewToggle from "@/components/ViewToggle.vue";
import { PluckItem, safeArray, uniq } from "@/helpers/functions/ArrayHelpers";
import { deepCopy } from "@/helpers/functions/ObjectHelpers";
import PasswordForm from "@/modules/users/Components/PasswordForm.vue";
import SelectMultiMunicipio from "@/modules/users/Components/SelectMultiMunicipio.vue";

export default {
  mixins: [validationMixin],
  components: {
    ViewToggle,
    PasswordForm,
    SelectMultiMunicipio,
  },
  data: () => ({
    roles: null,
    cliente: null,
    serverError: false,
    viewPassword: false,
    viewPasswordConfirmation: false,
  }),
  validations: {
    user: {
      name: {
        required,
        minLength: minLength(3),
      },
      email: {
        required,
        email,
        serverFailed: helpers.withParams({ message: [] }, function (value, vm) {
          return !this.serverError;
        }),
      },
      cliente_id: {
        required,
      },
      password: {
        required: requiredIf(function (user) {
          return user.id ? false : true;
        }),
        minLength: minLength(6),
      },
      password_confirmation: {
        sameAsPassword: sameAs("password"),
      },
    },
  },
  computed: {
    ...mapState("users", ["user"]),
    selectedRoles: {
      set(ids) {
        const safeIds = safeArray(ids);
        const allRoles = safeArray(deepCopy(this.roles));
        const marked = allRoles.filter((role) => safeIds.includes(role.id));

        this.user.roles = marked;
      },
      get() {
        const roles = deepCopy(this.user?.roles);

        return PluckItem(roles, "id");
      },
    },
    canUseMap() {
      const allowedRoles = ["simular_perfil"];
      const activeRoles = PluckItem(this.user.roles, "name");

      return allowedRoles.every((role) => activeRoles.includes(role));
    },
    formattedRoles() {
      return safeArray(this.roles).map((role) => ({
        text: role.name,
        value: role.id,
      }));
    },
    isActiveUserSimulatedOption() {
      return PluckItem(this.user.roles, "name").includes("simular_usuario");
    },
  },
  methods: {
    ...mapActions("acl", ["ActionFind"]),
    handlerChangeField(remove = false) {
      const source = this.user || {};
      const fields = ["password", "password_confirmation"];

      fields.forEach((field) => {
        if (Reflect.has(source, field)) {
          if (remove) delete source[field];
          else source[field] = "";
        }
      });
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.user[name];
      return $dirty ? !$error : null;
    },
    isValid() {
      this.$v.user.$touch();
      if (!this.canUseMap) {
        this.user.simulacao_recorte = {
          regioes: [],
          estados: [],
          municipios: [],
        };
      }

      const isValidPassRef = this.$refs?.passwordFormRef?.validate?.() ?? true;

      return isValidPassRef && !this.$v.user.$anyError;
    },
  },
  watch: {
    "user.cliente.id": {
      handler: function (value, old) {
        this.user.cliente_id = value;
      },
    },
    isActiveUserSimulatedOption(value) {
      if (!value) return;

      const chainActiveRoles = ["simular_usuario", "simular_perfil"];
      const toActiveRoles = this.formattedRoles.filter((item) =>
        chainActiveRoles.includes(item.text)
      );

      const chainActiveIds = PluckItem(toActiveRoles, "value");
      this.selectedRoles = uniq([...this.selectedRoles, ...chainActiveIds]);
    },
  },
  async created() {
    const response_roles = await this.ActionFind({ per_page: 30 });
    this.roles = response_roles.data;
  },
};
</script>
<style scoped lang="scss"></style>
